import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {SaveSaleVehicleLeasingBody, useSaveSaleVehicleLeasingMutation} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {PendingEditorChangeHandler} from '../types/PendingEditor';
import {areLeasingBodiesEqual} from '../utils/areLeasingBodiesEqual';
import {findInvalidLeasingMoneyKey} from '../utils/findInvalidLeasingMoneyKey';
import {hasValidActiveLeasingCell} from '../utils/hasValidActiveLeasingCell';
import {isLeasingDraftDirty} from '../utils/isLeasingDraftDirty';
import {DraftSaveRequest, useQueuedDraftSave} from './useQueuedDraftSave';
import {useStablePendingEditor} from './useStablePendingEditor';

interface UseVehicleLeasingDraftParams {
  saleVehicleId: string;
  baselineBody: SaveSaleVehicleLeasingBody | null;
  isWriteAllowed: boolean;
  onPendingChange?: PendingEditorChangeHandler;
  onSavingChange?: (isSaving: boolean) => void;
  onValidationError?: () => void;
}

interface VehicleLeasingDraft {
  draft: SaveSaleVehicleLeasingBody | null;
  financialInstitutionError: string | null;
  isActiveSelectionErrorVisible: boolean;
  isSaving: boolean;
  isDirty: boolean;
  setDraft: (nextDraft: SaveSaleVehicleLeasingBody) => void;
  discardDraft: () => void;
  saveDraft: () => Promise<boolean>;
}

/**
 * The leasing draft engine: the unsaved body, the dirty diff against the persisted configuration,
 * the validation gates a save has to clear, and the pending editor published to the surrounding
 * page. `useQueuedDraftSave` owns the save attempt around the request itself.
 */
export function useVehicleLeasingDraft(params: UseVehicleLeasingDraftParams): VehicleLeasingDraft {
  const [draft, setDraftState] = useState<SaveSaleVehicleLeasingBody | null>(null);
  const [financialInstitutionError, setFinancialInstitutionError] = useState<string | null>(null);
  const [isActiveSelectionErrorVisible, setIsActiveSelectionErrorVisible] = useState(false);
  /** The draft as of the latest publish, safe to read from asynchronous continuations. */
  const draftRef = useRef<SaveSaleVehicleLeasingBody | null>(null);
  const [saveLeasing, saveStatus] = useSaveSaleVehicleLeasingMutation();
  const pendingEditor = useStablePendingEditor(params.onPendingChange);
  const saveDraft = useQueuedDraftSave({
    prepareRequest: prepareDraftRequest,
    onSavingChange: params.onSavingChange,
    errorOptions: {
      callback: ({validationErrors}) => {
        const providerError = validationErrors?.find(
          ({name}) => name === 'financialInstitutionCodeId'
        );

        if (isNotNil(providerError)) {
          setFinancialInstitutionError(providerError.message);
        }
      },
    },
  });

  /**
   * Retracts the messages the draft has since resolved.
   *
   * The provider message is retracted when the provider itself changes, not on any edit that
   * leaves a provider set: the endpoint reports its own message against the provider that was
   * sent, and that message stays true until the user picks a different one.
   */
  function clearResolvedErrors(nextDraft: SaveSaleVehicleLeasingBody | null) {
    const currentProvider =
      (draftRef.current ?? params.baselineBody)?.financialInstitutionCodeId ?? null;

    if (isNil(nextDraft) || (nextDraft.financialInstitutionCodeId ?? null) !== currentProvider) {
      setFinancialInstitutionError(null);
    }

    if (isNil(nextDraft) || hasValidActiveLeasingCell(nextDraft)) {
      setIsActiveSelectionErrorVisible(false);
    }
  }

  function publishDraft(nextDraft: SaveSaleVehicleLeasingBody | null) {
    clearResolvedErrors(nextDraft);

    const dirtyDraft = isLeasingDraftDirty(params.baselineBody, nextDraft) ? nextDraft : null;

    draftRef.current = dirtyDraft;
    setDraftState(dirtyDraft);
    pendingEditor.publishPendingEditor(
      isNotNil(dirtyDraft) ? {save: saveDraft, discard: () => publishDraft(null)} : null
    );
  }

  /**
   * Describes what a save attempt has to send, from the draft as it stands when its turn comes.
   * A draft without a provider, without an active variant, or with a money amount the endpoint
   * would reject rejects the attempt, because none of those gaps can be resolved on the user's
   * behalf.
   */
  function prepareDraftRequest(): DraftSaveRequest {
    const pendingDraft = draftRef.current;

    if (isNil(pendingDraft)) {
      return {type: 'NOTHING_TO_SEND'};
    }

    return (
      match<SaveSaleVehicleLeasingBody, DraftSaveRequest>(pendingDraft)
        // Defence in depth behind the disabled editors: a save requested without the write
        // permission is refused here, so no programmatic call can reach the endpoint.
        .when(
          () => !params.isWriteAllowed,
          () => ({type: 'REJECTED'})
        )
        .when(
          (pending) => isNilOrEmpty(pending.financialInstitutionCodeId),
          () => reject(() => setFinancialInstitutionError(i18n.t('general.errors.mixed.required')))
        )
        .when(
          (pending) => !hasValidActiveLeasingCell(pending),
          () => reject(() => setIsActiveSelectionErrorVisible(true))
        )
        .when(
          (pending) => isNotNil(findInvalidLeasingMoneyKey(pending)),
          // The offending input already shows its own message, so the notification only has to say
          // that something is invalid.
          () => reject(() => showNotification.error(i18n.t('general.errors.mixed.invalid')))
        )
        .otherwise((pending) => ({type: 'SEND', send: () => sendDraft(pending)}))
    );
  }

  function reject(reportReason: () => void): DraftSaveRequest {
    reportReason();
    params.onValidationError?.();

    return {type: 'REJECTED'};
  }

  function sendDraft(pendingDraft: SaveSaleVehicleLeasingBody) {
    return saveLeasing({saleVehicleId: params.saleVehicleId, body: pendingDraft})
      .unwrap()
      .then(() => {
        // Keep an edit made while the request was in flight: only the saved draft is cleared.
        const currentDraft = draftRef.current;

        if (isNotNil(currentDraft) && areLeasingBodiesEqual(currentDraft, pendingDraft)) {
          publishDraft(null);
        }
      });
  }

  return {
    draft,
    financialInstitutionError,
    isActiveSelectionErrorVisible,
    isSaving: saveStatus.isLoading,
    isDirty: isLeasingDraftDirty(params.baselineBody, draft),
    setDraft: publishDraft,
    discardDraft: () => publishDraft(null),
    saveDraft,
  };
}
